import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, setWorkshopCode } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogIn, UserPlus, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo, BRAND } from "@/components/Brand";

function GoogleButton({ label = "Continuar con Google", disabled }) {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button"
      variant="outline"
      onClick={start}
      disabled={disabled}
      className="h-12 w-full text-base border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
      data-testid="google-signin-btn"
    >
      <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5 17.6 35.5 12.5 30.4 12.5 24S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.2 0 19-7.4 19-19.5 0-1.3-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 16.3 4.5 9.7 8.7 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-2 13.2-5.2l-6.1-5.2c-2 1.5-4.5 2.4-7.1 2.4-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.5 39 16.2 43.5 24 43.5z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4.1 5.3l6.1 5.2c-.4.4 6.5-4.7 6.5-14.5 0-1.3-.1-2.3-.4-3.5z"/>
      </svg>
      {label}
    </Button>
  );
}

export default function Welcome() {
  const { setWorkshop } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next") || "/panel";
  const initialTab = params.get("tab") === "register" ? "register" : "login";
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");

  // Register form
  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");
  const [regName, setRegName] = useState("");
  const [regOwner, setRegOwner] = useState("");
  const [regAccept, setRegAccept] = useState(false);

  // Legacy code
  const [legacyCode, setLegacyCode] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPass) { toast.error("Completa email y contraseña"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: loginEmail.trim(), password: loginPass });
      setWorkshop(data);
      toast.success(`Bienvenido, ${data.name}`);
      navigate(nextParam);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo iniciar sesión"));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!regEmail.trim() || regPass.length < 6 || !regName.trim()) {
      toast.error("Rellena todos los campos (contraseña mín. 6)");
      return;
    }
    if (!regAccept) {
      toast.error("Debes aceptar los términos y la política de privacidad");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: regEmail.trim(),
        password: regPass,
        workshop_name: regName.trim(),
        owner_name: regOwner.trim() || "",
        accept_legal: true,
      });
      setWorkshop(data);
      toast.success(`Taller creado. ¡90 días de prueba activados!`);
      navigate(nextParam);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo crear la cuenta"));
    } finally {
      setLoading(false);
    }
  };

  const doLegacy = async (e) => {
    e.preventDefault();
    const upper = legacyCode.trim().toUpperCase();
    if (!upper) { toast.error("Introduce el código"); return; }
    setLoading(true);
    try {
      const res = await api.get(`/workshop/validate?code=${encodeURIComponent(upper)}`);
      setWorkshopCode(res.data.code);
      const me = await api.get("/auth/me");
      setWorkshop(me.data);
      toast.success(`Bienvenido, ${res.data.name}`);
      navigate(nextParam);
    } catch (e) {
      toast.error(errMsg(e, "Código no válido"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="welcome-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center text-white space-y-3">
          <div className="flex justify-center">
            <Logo size={72} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{BRAND.name}</h1>
          <p className="text-slate-300 text-sm">Agenda inteligente · 3 meses gratis · 35 € + IVA/año</p>
          <button
            onClick={() => navigate("/")}
            className="text-slate-400 hover:text-white text-xs inline-flex items-center gap-1"
            data-testid="back-to-landing"
          >
            <ArrowLeft className="w-3 h-3" /> Volver a la web
          </button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-2xl">Accede a tu taller</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <GoogleButton disabled={loading} />
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"/></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">o con email</span></div>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-3 w-full h-11">
                <TabsTrigger value="login" data-testid="tab-login"><LogIn className="w-4 h-4 mr-1"/>Entrar</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register"><UserPlus className="w-4 h-4 mr-1"/>Crear</TabsTrigger>
                <TabsTrigger value="code" data-testid="tab-code"><KeyRound className="w-4 h-4 mr-1"/>Código</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="pt-4">
                <form onSubmit={doLogin} className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="lemail">Email</Label>
                    <Input id="lemail" type="email" autoComplete="email" value={loginEmail} onChange={(e)=>setLoginEmail(e.target.value)} className="h-11" data-testid="login-email"/>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="lpass">Contraseña</Label>
                    <Input id="lpass" type="password" autoComplete="current-password" value={loginPass} onChange={(e)=>setLoginPass(e.target.value)} className="h-11" data-testid="login-password"/>
                  </div>
                  <Button type="submit" disabled={loading} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="login-submit">
                    {loading ? "Entrando..." : "Entrar"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="pt-4">
                <form onSubmit={doRegister} className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="rname">Nombre del taller</Label>
                    <Input id="rname" value={regName} onChange={(e)=>setRegName(e.target.value)} placeholder="Taller Pérez" className="h-11" data-testid="register-workshop-name"/>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rowner">Tu nombre (opcional)</Label>
                    <Input id="rowner" value={regOwner} onChange={(e)=>setRegOwner(e.target.value)} placeholder="Juan Pérez" className="h-11" data-testid="register-owner"/>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="remail">Email</Label>
                    <Input id="remail" type="email" autoComplete="email" value={regEmail} onChange={(e)=>setRegEmail(e.target.value)} className="h-11" data-testid="register-email"/>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rpass">Contraseña (mín. 6)</Label>
                    <Input id="rpass" type="password" autoComplete="new-password" value={regPass} onChange={(e)=>setRegPass(e.target.value)} className="h-11" data-testid="register-password"/>
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={regAccept}
                      onChange={(e) => setRegAccept(e.target.checked)}
                      className="mt-0.5"
                      data-testid="register-accept-legal"
                    />
                    <span>
                      He leído y acepto los{" "}
                      <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Términos de uso</a>,{" "}
                      la{" "}
                      <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Política de privacidad</a>{" "}
                      y las{" "}
                      <a href="/contratacion" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Condiciones de contratación</a>.
                    </span>
                  </label>
                  <Button type="submit" disabled={loading} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="register-submit">
                    {loading ? "Creando..." : "Crear taller · 3 meses gratis"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="code" className="pt-4">
                <form onSubmit={doLegacy} className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="lcode">Código del taller (versión anterior)</Label>
                    <Input id="lcode" value={legacyCode} onChange={(e)=>setLegacyCode(e.target.value.toUpperCase())} placeholder="DEMO" className="h-11 font-mono uppercase" data-testid="legacy-code-input"/>
                  </div>
                  <Button type="submit" disabled={loading} className="h-11 w-full bg-slate-700 hover:bg-slate-800" data-testid="legacy-code-submit">
                    Entrar con código
                  </Button>
                </form>
                <p className="text-xs text-slate-500 mt-2">Los talleres registrados antes de la v2 conservan acceso vitalicio con su código.</p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-slate-400 text-xs">
          Cuentas creadas hoy incluyen 90 días de prueba con acceso completo.
        </p>
      </div>
    </div>
  );
}
